import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { When } from 'react-if';
import { DateTime } from 'luxon';
import { Tooltip } from '@mui/material';
import {
  ChevronDownIcon,
  CloudArrowUpIcon,
  PencilIcon,
  PhoneIcon,
} from '@heroicons/react/20/solid';
import {
  activityLabel,
  INote,
  NoteSyncStatus,
} from 'shared';

import { classNames } from '../../../common/lib/classNames';
import { IHeroIcon } from '../../types/types';

import { NoteAttachments } from './NoteAttachments';
import { CallTranscript } from './CallTranscript';

export interface IFeedItem {
  key: string;
  at: Date;
  icon: IHeroIcon;
  iconClass: string;
  title: React.ReactNode;
  detail?: React.ReactNode;
  /** Detail is hidden until the title is clicked */
  collapsible?: boolean;
  body?: React.ReactNode;
  /** Overrides the HH:mm time shown on the right */
  timeLabel?: string;
  pending?: boolean;
}

const syncClass: Partial<Record<NoteSyncStatus, string>> = {
  [NoteSyncStatus.NOT_SYNCED]: `text-orange-400`,
  [NoteSyncStatus.DEFFERED]: `text-orange-400`,
  [NoteSyncStatus.SYNCED]: `text-green-500`,
  [NoteSyncStatus.ERROR]: `text-red-600`,
};

/** A note, or a logged activity with its note, as a feed item */
export function noteItem(note: INote, accountingName: string): IFeedItem {
  const name = `${note.userFirstName || ``} ${note.userLastName || ``}`.trim() || `Someone`;
  const activityText = note.activity ? activityLabel(note.activity.type, note.activity.callOutcome) : null;
  const syncTooltip = note.syncStatus === NoteSyncStatus.SYNCED ? `Synced with ${accountingName}`
    : note.syncStatus === NoteSyncStatus.ERROR ? (note.syncError || `Unknown Sync Error`)
      : `Note not yet synced with ${accountingName}`;

  return {
    key: `note-${note.id}`,
    at: new Date(note.activity?.activityTime || note.createdAt),
    icon: note.activity ? PhoneIcon : PencilIcon,
    iconClass: `bg-paidniceGreen-600`,
    title: (
      <span className={ `inline-flex items-center gap-1` }>
        <span>
          <span className={ `font-medium` }>{ name }</span>
          { activityText ? ` logged a ${activityText}` : ` added a note` }
        </span>
        <When condition={ !!syncClass[note.syncStatus] }>
          <Tooltip title={ syncTooltip }>
            <CloudArrowUpIcon className={ classNames(`h-4 w-4`, syncClass[note.syncStatus]) } />
          </Tooltip>
        </When>
      </span>
    ),
    body: (note.content || note.attachments?.length) ? (
      <>
        { note.content }
        <NoteAttachments attachments={ note.attachments } />
        { note.activity?.contactCallId && (
          <CallTranscript
            organisationId={ note.organisationId }
            contactId={ note.contactId }
            contactCallId={ note.activity.contactCallId }
            callerName={ note.userFirstName }
          />
        ) }
      </>
    ) : null,
  };
}

interface IActivityFeedTimelineProps {
  /** Already filtered, oldest first */
  items: IFeedItem[];
  timezone?: string;
  /** Jumps back to the bottom when this changes, e.g. the active filter or after the user adds an item */
  followKey?: string;
  /** Rendered under the last day, inside the scroll area */
  footer?: React.ReactNode;
  /** Shows a loading line instead of the items, e.g. while the feed's sources are still arriving */
  loading?: boolean;
}

/** Day grouped thread with an icon per item on a connector line. Scrolls itself and follows new items at the bottom */
export function ActivityFeedTimeline({ items, timezone, followKey, footer, loading }: IActivityFeedTimelineProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  // Follow new items while the user is at the bottom of the thread
  const stickToBottom = useRef(true);
  const previousFollowKey = useRef(followKey);

  const days = useMemo(() => {
    const today = DateTime.now().setZone(timezone).toISODate();
    const groups: { key: string; label: string; items: IFeedItem[] }[] = [];

    items.forEach(item => {
      const date = DateTime.fromJSDate(item.at).setZone(timezone);
      const key = date.toISODate();
      let group = groups[groups.length - 1];

      if (!group || group.key !== key) {
        const label = key === today ? `Today`
          : date.toFormat(date.year === DateTime.now().year ? `ccc d LLL` : `ccc d LLL yyyy`);

        group = { key, label, items: [] };
        groups.push(group);
      }

      group.items.push(item);
    });

    return groups;
  }, [items, timezone]);

  useLayoutEffect(() => {
    const el = scrollRef.current;

    if (previousFollowKey.current !== followKey) {
      previousFollowKey.current = followKey;
      stickToBottom.current = true;
    }

    if (el && stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [days, followKey]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  return (
    <div
      ref={ scrollRef }
      onScroll={ onScroll }
      className={ `flex min-h-0 flex-1 flex-col overflow-auto px-4 pb-3 pt-1` }
    >
      <When condition={ days.length === 0 || loading }>
        <p className={ `py-8 text-center text-sm text-gray-500` }>{ loading ? `Loading...` : `Nothing here yet` }</p>
      </When>
      { !loading && days.map(day => (
        <div key={ day.key }>
          <div className={ `flex items-center gap-2.5 pb-2 pt-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-400` }>
            { day.label }
            <span className={ `h-px flex-1 bg-gray-100` } />
          </div>
          <div className={ `ml-2.5 flex flex-col border-l border-gray-200` }>
            { day.items.map(item => (
              <div
                key={ item.key }
                className={ classNames(
                  `relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2.5 pb-3.5 pl-5 text-[0.8125rem] text-gray-900`,
                  item.pending && `text-gray-500`,
                ) }
              >
                <span className={ classNames(
                  `absolute -left-2.5 top-0 flex h-5 w-5 items-center justify-center rounded-full text-white ring-[0.1875rem] ring-white`,
                  item.iconClass,
                ) }>
                  <item.icon className={ `h-3 w-3` } />
                </span>
                <div className={ `flex min-w-0 flex-col gap-0.5` }>
                  { item.collapsible ? (
                    <button
                      type={ `button` }
                      onClick={ () => setExpanded(current => ({ ...current, [item.key]: !current[item.key] })) }
                      aria-expanded={ !!expanded[item.key] }
                      className={ `cursor-pointer flex items-center gap-1 text-left leading-[1.4] hover:text-paidniceGreen-700` }
                    >
                      { item.title }
                      <ChevronDownIcon className={ classNames(`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform`, expanded[item.key] && `rotate-180`) } />
                    </button>
                  ) : (
                    <div className={ classNames(`leading-[1.4] [&_*]:text-[length:inherit]`, item.pending && `italic`) }>{ item.title }</div>
                  ) }
                  { item.detail && (!item.collapsible || expanded[item.key]) ? (
                    <div className={ `whitespace-pre-line text-xs leading-[1.45] text-gray-500` }>{ item.detail }</div>
                  ) : null }
                  { item.body ? (
                    <div className={ `whitespace-pre-wrap break-words rounded-lg bg-paidniceGreen-50 px-2.5 py-1.5 text-paidniceGreen-900` }>
                      { item.body }
                    </div>
                  ) : null }
                </div>
                <Tooltip title={ DateTime.fromJSDate(item.at).setZone(timezone).toFormat(`yyyy-MM-dd HH:mm`) }>
                  <span className={ `whitespace-nowrap text-xs text-gray-400` }>
                    { item.timeLabel || DateTime.fromJSDate(item.at).setZone(timezone).toFormat(`HH:mm`) }
                  </span>
                </Tooltip>
              </div>
            )) }
          </div>
        </div>
      )) }
      { footer }
    </div>
  );
}

interface IFeedFilterProps<T extends string> {
  filters: { id: T; label: string }[];
  value: T;
  onChange: (value: NoInfer<T>) => void;
}

/** Segmented control used for the feed filters and the composer type */
export function SegmentedControl<T extends string>({ filters, value, onChange }: IFeedFilterProps<T>) {
  return (
    <div className={ `flex shrink-0 gap-0.5 rounded-lg bg-gray-100 p-0.5` }>
      { filters.map(f => (
        <button
          key={ f.id }
          type={ `button` }
          onClick={ () => onChange(f.id) }
          className={ classNames(`cursor-pointer`,
            `rounded-md px-2.5 py-1 text-xs font-medium`,
            value === f.id ? `bg-white text-gray-900 shadow-sm` : `text-gray-500 hover:text-gray-700`,
          ) }
        >
          { f.label }
        </button>
      )) }
    </div>
  );
}
